/**
 * Canvas API — workbooks, graph operations, events, manual adjustments, regeneration.
 */
import { apiClient, buildUrl } from './apiClient';
import type {
  CanvasGraphModel,
  CanvasGraphResponse,
  CanvasEventModel,
  CanvasEventsResponse,
  RecordCanvasEventRequest,
  CreateManualAdjustmentRequest,
  ManualAdjustmentActionRequest,
  EdgeRegenerateRequest,
  EdgeRegenerateResponseModel,
  TransformationTestResponse,
} from './types';

export async function listCanvasWorkbooks(): Promise<string[]> {
  const response = await apiClient.get<string[]>(buildUrl('/api/v2/mapping-canvas/workbooks'));
  return response.data;
}

export async function getCanvasGraph(workbookId: string): Promise<CanvasGraphModel> {
  const response = await apiClient.get<CanvasGraphResponse>(
    buildUrl(`/api/v2/mapping-canvas/workbooks/${workbookId}/graph`)
  );
  return response.data.graph;
}

export async function saveCanvasGraph(workbookId: string, graph: CanvasGraphModel, actor: string = 'web-ui'): Promise<CanvasGraphModel> {
  const response = await apiClient.post<CanvasGraphResponse>(
    buildUrl(`/api/v2/mapping-canvas/workbooks/${workbookId}/graph?actor=${encodeURIComponent(actor)}`),
    graph
  );
  return response.data.graph;
}

export async function listCanvasEvents(workbookId: string): Promise<CanvasEventModel[]> {
  const response = await apiClient.get<CanvasEventsResponse>(
    buildUrl(`/api/v2/mapping-canvas/workbooks/${workbookId}/events`)
  );
  return response.data.events;
}

export async function recordCanvasEvent(workbookId: string, payload: RecordCanvasEventRequest): Promise<CanvasEventModel> {
  const response = await apiClient.post<CanvasEventModel>(
    buildUrl(`/api/v2/mapping-canvas/workbooks/${workbookId}/events`),
    payload
  );
  return response.data;
}

export async function createCanvasManualAdjustment(
  workbookId: string,
  payload: CreateManualAdjustmentRequest
): Promise<CanvasGraphModel> {
  const response = await apiClient.post<CanvasGraphResponse>(
    buildUrl(`/api/v2/mapping-canvas/workbooks/${workbookId}/manual-adjustments`),
    payload
  );
  return response.data.graph;
}

export async function undoCanvasManualAdjustment(
  workbookId: string,
  adjustmentId: string,
  payload: ManualAdjustmentActionRequest
): Promise<CanvasGraphModel> {
  const response = await apiClient.post<CanvasGraphResponse>(
    buildUrl(
      `/api/v2/mapping-canvas/workbooks/${workbookId}/manual-adjustments/${adjustmentId}/undo`
    ),
    payload
  );
  return response.data.graph;
}

export async function redoCanvasManualAdjustment(
  workbookId: string,
  adjustmentId: string,
  payload: ManualAdjustmentActionRequest
): Promise<CanvasGraphModel> {
  const response = await apiClient.post<CanvasGraphResponse>(
    buildUrl(
      `/api/v2/mapping-canvas/workbooks/${workbookId}/manual-adjustments/${adjustmentId}/redo`
    ),
    payload
  );
  return response.data.graph;
}

export async function regenerateCanvasEdge(
  workbookId: string,
  edgeId: string,
  payload: EdgeRegenerateRequest
): Promise<EdgeRegenerateResponseModel> {
  const response = await apiClient.post<EdgeRegenerateResponseModel>(
    buildUrl(`/api/v2/mapping-canvas/workbooks/${workbookId}/edges/${edgeId}/regenerate`),
    payload
  );
  return response.data;
}

export async function runPreviewJob(
  batchId: string,
  jobId: string,
  payload: {
    recipe: Record<string, unknown>;
    plan?: Record<string, unknown>;
    sample_size?: number;
    target_tables?: string[];
  }
): Promise<TransformationTestResponse> {
  const response = await apiClient.post(
    buildUrl(`/api/v2/mapping-canvas/preview/jobs/${jobId}/callback`),
    {
      batch_id: batchId,
      job_id: jobId,
      recipe: payload.recipe,
      plan: payload.plan || { version: '3.0' },
      sample_size: payload.sample_size,
      target_tables: payload.target_tables,
    }
  );
  return response.data as unknown as TransformationTestResponse;
}

export async function getGeneratedSql(
  recipeId: string,
  dialect: string = 'duckdb'
): Promise<Record<string, string>> {
  const response = await apiClient.get<Record<string, string>>(
    buildUrl(`/api/v2/mapping-canvas/recipes/${recipeId}/sql`),
    { params: { dialect } }
  );
  return response.data;
}
